import sessionReplayImg from 'common/assets/images/tour-images/session-replay-tour.png';
import globalSidenavImg from 'common/assets/images/tour-images/global-sidenav-tour.png';
import b2bAnalyticsTourSummaryImg from 'common/assets/images/tour-images/b2b-tour/account-tour-summary.png';
import b2bAnalyticsStep1Img from 'common/assets/images/tour-images/b2b-tour/account-tour-1.png';
import b2bAnalyticsStep2Img from 'common/assets/images/tour-images/b2b-tour/account-tour-2.png';
import b2bAnalyticsStep3Img from 'common/assets/images/tour-images/b2b-tour/account-tour-3.png';
import b2bAnalyticsStep4Img from 'common/assets/images/tour-images/b2b-tour/account-tour-4.png';
import {LOCALES, Translatable} from 'common/i18n';
import {FeatureGateTier} from 'common/report/constants';
import {promptFeedbackModal} from 'common/widgets/feedback-modal';
import {FeedbackForFeature} from 'common/widgets/feedback-modal/types';

export interface TourStepData {
  title: Translatable;
  description: Translatable;
  tourImage?: string;
  secondaryDescription?: Translatable;
  primaryButtonText?: Translatable;
  primaryButtonLink?: string;
  secondaryButtonText?: Translatable;
  secondaryButtonLink?: string;
  displayDismissButton?: boolean;
  onClickPrimaryButton?: () => void;
}

export interface TourData {
  stepData: TourStepData[];
  expirationDate?: Date;
  featureGateTier?: string[];
  maxWidth?: string;
  includeFirstStepInStepCount?: boolean;
}

export const TOURS = {
  'b2b-analytics': {
    stepData: [
      {
        title: ({t}) => t(`tours.b2b-analytics.step-0.title`),
        description: ({t}) => t(`tours.b2b-analytics.step-0.description`),
        primaryButtonText: ({t}) => t(`tours.b2b-analytics.step-0.next`),
        displayDismissButton: true,
        tourImage: b2bAnalyticsTourSummaryImg,
      },
      {
        title: ({t}) => t(`tours.b2b-analytics.step-1.title`),
        description: ({t}) => t(`tours.b2b-analytics.step-1.description`),
        primaryButtonText: ({t}) => t(`tours.b2b-analytics.step-1.next`),
        tourImage: b2bAnalyticsStep1Img,
      },
      {
        title: ({t}) => t(`tours.b2b-analytics.step-2.title`),
        description: ({t}) => t(`tours.b2b-analytics.step-2.description`),
        primaryButtonText: ({t}) => t(`tours.b2b-analytics.step-2.next`),
        secondaryButtonText: ({t}) => t(`tours.b2b-analytics.step-2.cancel`),
        tourImage: b2bAnalyticsStep2Img,
      },
      {
        title: ({t}) => t(`tours.b2b-analytics.step-3.title`),
        description: ({t}) => t(`tours.b2b-analytics.step-3.description`),
        primaryButtonText: ({t}) => t(`tours.b2b-analytics.step-3.next`),
        secondaryButtonText: ({t}) => t(`tours.b2b-analytics.step-3.cancel`),
        tourImage: b2bAnalyticsStep3Img,
      },
      {
        title: ({t}) => t(`tours.b2b-analytics.step-4.title`),
        description: ({t}) => t(`tours.b2b-analytics.step-4.description`),
        primaryButtonText: ({t}) => t(`tours.b2b-analytics.step-4.next`),
        secondaryButtonText: ({t}) => t(`tours.b2b-analytics.step-4.cancel`),
        displayDismissButton: true,
        tourImage: b2bAnalyticsStep4Img,
        primaryButtonLink: `https://docs.mixpanel.com/docs/data-structure/group-analytics`,
      },
    ],
  },
  'consistent-colors': {
    maxWidth: `300px`,
    stepData: [
      {
        secondaryButtonLink: `https://docs.mixpanel.com/docs/features/chart-customization#segment-coloring`,
        title: ({t}) => t(`tours.consistent-colors.title`),
        description: ({t}) => t(`tours.consistent-colors.description`),
        primaryButtonText: ({t}) => t(`tours.consistent-colors.cta`),
        secondaryButtonText: ({t}) => t(`tours.cta-docs`),
      },
    ],
  },
  'global-sidenav': {
    stepData: [
      {
        title: ({t}) => t(`tours.global-sidenav.title`),
        description: ({t}) => t(`tours.global-sidenav.description`),
        tourImage: globalSidenavImg,
        displayDismissButton: true,
        primaryButtonText: ({t}) => t(`tours.global-sidenav.learn-more`),
        primaryButtonLink: `https://www.loom.com/share/f2128bcf80ae455ebc7b568f49dc3df8?sid=1dcdce2f-1ac5-47f8-a62a-9865113be0cd`,
      },
    ],
    expirationDate: new Date(`2025-04-30T23:59:59Z`),
  },
  sessionReplayFreeTour: {
    stepData: [
      {
        title: ({t}) => t(`tours.session-replay.title`),
        description: ({t}) => t(`tours.session-replay.description-free`),
        tourImage: sessionReplayImg,
        secondaryDescription: ({t}) => t(`tours.session-replay.description-2`),
        primaryButtonText: ({t}) => t(`common.learn-more`),
        primaryButtonLink: `https://mixpanel.com/releases/session-replay/`,
        secondaryButtonText: ({t}) => t(`tours.cta-docs`),
        secondaryButtonLink: `https://docs.mixpanel.com/docs/session-replay/session-replay-web`,
        displayDismissButton: true,
      },
    ],
    expirationDate: new Date(`2024-12-15T23:59:59Z`),
    featureGateTier: [FeatureGateTier.Free, FeatureGateTier.Business],
  },
  localization: {
    stepData: [
      {
        title: ({t}) => t(`tours.localization.title`),
        description: ({t, language}) =>
          t(`tours.localization.description`, {
            currentLanguage: LOCALES.find((locale) => locale.code === language)?.displayName,
          }),
        primaryButtonText: ({t}) => t(`help.submit-feedback`),
        onClickPrimaryButton: () =>
          promptFeedbackModal({
            feature: FeedbackForFeature.Localization,
          }),
        displayDismissButton: true,
      },
    ],
  },
} as const satisfies Record<string, TourData>;

export type TourName = keyof typeof TOURS;
